import { ComponentType, CSSProperties, ReactNode, SVGProps, useLayoutEffect, useRef, useState } from 'react';

import {
  BookOpenIcon,
  CalendarPlusIcon,
  CheckCircleIcon,
  ClockCheckIcon,
  CloseIcon,
  DiscountIcon,
  FaceContentIcon,
  FileDownloadIcon,
  GlobeIcon,
  TimeIcon,
  TranslateIcon,
  VoiceIcon,
} from '@braskai/icons';

import { useMediaQuery } from '@mui/material';

import { UpsellContext, UpsellOffer, UpsellTierOffer, UpsellUsage } from '@/entities/pricing';

import { useInterfaceLanguage } from '@/shared/i18n';
import { ResultDownloadKind } from '@/shared/lib/resultDownload';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';

import {
  getDownloadCopy,
  getDownloadDoneLabel,
  getTierMultiplierLabel,
  getUpsellCtaLabel,
  getUpsellSecondaryLabel,
  getUsageCopy,
  getUsageLine,
  getWelcomeCopy,
  UpsellIcon,
  UpsellRow,
} from '../../lib/upsell/getUpsellCopy';
import { getUpsellAnalytics } from '../../model/upsell/analytics';
import { useUpsellCheckout } from '../../model/upsell/useUpsellCheckout';
import { UpsellHighlight, useUpsellModalStore } from '../../model/upsell/useUpsellModalStore';
import verticalPoster from './assets/translate-vertical-poster.jpg';
import verticalMp4 from './assets/translate-vertical.mp4';
import verticalWebm from './assets/translate-vertical.webm';
import {
  HERO_WIDTH,
  RowsVariant,
  SActions,
  SBody,
  SCaption,
  SCloseButton,
  SDialog,
  SDivider,
  SGhostButton,
  SHero,
  SLabel,
  SOfferText,
  SOfferTitle,
  SPrimaryButton,
  SRow,
  SRows,
  SScroll,
  SSecondaryLink,
  SSecondaryRow,
  STitle,
  SWelcomeCopy,
  SWelcomeGrid,
  SWelcomeMedia,
  UPSELL_MOBILE_QUERY,
} from './styled';
import { SubtitlesIcon } from './SubtitlesIcon';
import { AnnualHero, LipSyncHero, TierHero, VideoHero } from './UpsellHeroes';
import { UpsellVideo } from './UpsellVideo';

const ICONS: Record<UpsellIcon, ComponentType<SVGProps<SVGSVGElement>>> = {
  bookOpen: BookOpenIcon,
  calendarPlus: CalendarPlusIcon,
  checkCircle: CheckCircleIcon,
  clockCheck: ClockCheckIcon,
  discount: DiscountIcon,
  faceContent: FaceContentIcon,
  fileDownload: FileDownloadIcon,
  globe: GlobeIcon,
  subtitles: SubtitlesIcon,
  time: TimeIcon,
  translate: TranslateIcon,
  voice: VoiceIcon,
};

const TITLE_ID = 'upsell-modal-title';

const Rows = ({ rows, variant }: { rows: UpsellRow[]; variant: RowsVariant }) => (
  <SRows $variant={variant}>
    {rows.map(({ id, icon, text }) => {
      const Icon = ICONS[icon];

      return (
        <SRow key={id}>
          <Icon />
          {text}
        </SRow>
      );
    })}
  </SRows>
);

type UpsellDismissAction = 'backdrop' | 'close_button' | 'escape' | 'later' | 'start';

type ActionsProps = {
  context: UpsellContext;
  onClose: (action: UpsellDismissAction) => void;
  onSelect: (offer: UpsellOffer, isSecondary: boolean) => void;
};

const WelcomeContent = ({
  context,
  offer,
  onClose,
  onSelect,
}: { offer?: UpsellTierOffer } & ActionsProps) => {
  const { locale, t } = useInterfaceLanguage();
  const isMobile = useMediaQuery(UPSELL_MOBILE_QUERY);
  const copy = getWelcomeCopy(context, offer ?? null, t, locale);

  return (
    <SWelcomeGrid>
      <SWelcomeCopy>
        <STitle id={TITLE_ID}>{copy.title}</STitle>
        <SLabel>{t('upsell.welcome.includes')}</SLabel>
        <Rows rows={copy.rows} variant="welcome" />
        {offer && copy.offer ? (
          <>
            <SDivider />
            <SOfferTitle>{t('upsell.welcome.needMore')}</SOfferTitle>
            <SOfferText>{copy.offer.text}</SOfferText>
            <SRows $variant="extras">
              {copy.offer.extras.map((text) => (
                <SRow key={text} $strong>
                  <CheckCircleIcon />
                  {text}
                </SRow>
              ))}
            </SRows>
            <SActions $marginTop={25.6}>
              <SPrimaryButton
                $scale="welcome"
                data-testid="upsell-modal-cta"
                size="lg"
                onClick={() => onSelect(offer, false)}
              >
                {getUpsellCtaLabel(offer, t, locale)}
              </SPrimaryButton>
              <SGhostButton
                $scale="welcome"
                data-testid="upsell-modal-later"
                size="lg"
                variant="text"
                onClick={() => onClose('later')}
              >
                {t('upsell.action.maybeLater')}
              </SGhostButton>
            </SActions>
          </>
        ) : (
          <SActions $marginTop={32}>
            <SPrimaryButton $scale="welcome" data-testid="upsell-modal-start" size="lg" onClick={() => onClose('start')}>
              {t('upsell.action.startTranslating')}
            </SPrimaryButton>
          </SActions>
        )}
      </SWelcomeCopy>
      {!isMobile && (
        <SWelcomeMedia aria-hidden="true">
          <UpsellVideo mp4={verticalMp4} poster={verticalPoster} webm={verticalWebm} />
        </SWelcomeMedia>
      )}
    </SWelcomeGrid>
  );
};

const HeroFrame = ({ children }: { children: ReactNode }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const node = ref.current;

    if (!node || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(([entry]) => setScale(Math.min(1, entry.contentRect.width / HERO_WIDTH)));
    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  return (
    <SHero ref={ref} style={{ '--hero-scale': scale } as CSSProperties}>
      {children}
    </SHero>
  );
};

type OfferContentProps = {
  downloadKind?: ResultDownloadKind;
  flow: 'download' | 'usage';
  highlight?: UpsellHighlight;
  primary: UpsellOffer;
  secondary?: UpsellOffer;
  usage?: UpsellUsage;
} & ActionsProps;

const OfferHero = ({ context, flow, primary, usage }: Pick<OfferContentProps, 'context' | 'flow' | 'primary' | 'usage'>) => {
  const { locale, t } = useInterfaceLanguage();

  if (primary.kind === 'annual') {
    const usagePercent = flow === 'usage' && usage ? usage.percent : undefined;
    return <AnnualHero offer={primary} usagePercent={usagePercent} />;
  }

  if (flow === 'usage') {
    return (
      <TierHero
        currentCategory={context.category}
        multiplierLabel={getTierMultiplierLabel(context, primary, t, locale)}
        offer={primary}
        used={usage?.used ?? 0}
      />
    );
  }

  return primary.category === 'creator_pro' ? <LipSyncHero /> : <VideoHero />;
};

const OfferContent = ({
  context,
  downloadKind,
  flow,
  highlight,
  primary,
  secondary,
  usage,
  onClose,
  onSelect,
}: OfferContentProps) => {
  const { locale, t } = useInterfaceLanguage();
  const copy =
    flow === 'download' ? getDownloadCopy(primary, t, locale, highlight) : getUsageCopy(context, primary, t, locale);

  return (
    <>
      <HeroFrame>
        <OfferHero context={context} flow={flow} primary={primary} usage={usage} />
      </HeroFrame>
      <SBody $flow={flow}>
        {flow === 'download' ? (
          <SCaption>
            <CheckCircleIcon />
            {getDownloadDoneLabel(downloadKind, t)}
          </SCaption>
        ) : (
          usage && <SCaption>{getUsageLine(context, usage, t, locale)}</SCaption>
        )}
        <STitle id={TITLE_ID}>{copy.title}</STitle>
        <Rows rows={copy.rows} variant={flow} />
        <SActions $marginTop={28.5} $stack>
          <SPrimaryButton
            $scale="offer"
            data-testid="upsell-modal-cta"
            size="lg"
            fullWidth
            onClick={() => onSelect(primary, false)}
          >
            {getUpsellCtaLabel(primary, t, locale)}
          </SPrimaryButton>
          {flow === 'download' && (
            <SGhostButton
              $scale="offer"
              data-testid="upsell-modal-later"
              size="lg"
              variant="text"
              fullWidth
              onClick={() => onClose('later')}
            >
              {t('common.close')}
            </SGhostButton>
          )}
        </SActions>
        {flow === 'usage' && (
          <SSecondaryRow>
            {secondary && (
              <SSecondaryLink
                data-testid="upsell-modal-secondary"
                size="sm"
                variant="link"
                onClick={() => onSelect(secondary, true)}
              >
                {getUpsellSecondaryLabel(secondary, t)}
              </SSecondaryLink>
            )}
          </SSecondaryRow>
        )}
      </SBody>
    </>
  );
};

export const UpsellModal = () => {
  const { t } = useInterfaceLanguage();
  const { close, context, downloadKind, flow, highlight, isOpen, primary, secondary, usage } = useUpsellModalStore();
  const openCheckout = useUpsellCheckout();

  if (!isOpen || !context || !flow) return null;

  const analytics = getUpsellAnalytics(context, flow, primary);

  const handleClose = (action: UpsellDismissAction) => {
    MixpanelEvents.upsellModalDismissed({ ...analytics, action });
    close();
  };

  const handleSelect = (offer: UpsellOffer, isSecondary: boolean) => {
    const event = isSecondary ? MixpanelEvents.upsellModalSecondaryClick : MixpanelEvents.upsellModalCtaClick;
    event(getUpsellAnalytics(context, flow, offer));
    close();
    openCheckout(context, offer, flow);
  };

  return (
    <SDialog
      $wide={flow === 'welcome'}
      aria-labelledby={TITLE_ID}
      data-flow={flow}
      data-testid="upsell-modal"
      open
      onClose={(_, reason) => handleClose(reason === 'backdropClick' ? 'backdrop' : 'escape')}
    >
      <SCloseButton
        $wide={flow === 'welcome'}
        aria-label={t('common.close')}
        data-testid="upsell-modal-close"
        onClick={() => handleClose('close_button')}
      >
        <CloseIcon />
      </SCloseButton>
      <SScroll>
      {flow === 'welcome' ? (
        <WelcomeContent
          context={context}
          offer={primary?.kind === 'tier' ? primary : undefined}
          onClose={handleClose}
          onSelect={handleSelect}
        />
      ) : (
        primary && (
          <OfferContent
            context={context}
            downloadKind={downloadKind}
            flow={flow}
            highlight={highlight}
            primary={primary}
            secondary={secondary}
            usage={usage}
            onClose={handleClose}
            onSelect={handleSelect}
          />
        )
      )}
      </SScroll>
    </SDialog>
  );
};
